/**
 * Chat Box Component
 * Conversation-specific chat with real SSE streaming from x-agent
 *
 * PR-10: Wires chat tool events to update objects and invalidate caches
 */

import { useRef, useEffect, useState, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import type { SetStateAction } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useConversation } from '@/app/providers/ConversationContext';
import { useModulePane } from '@/app/providers/ModulePaneContext';
import { useChat, type ChatAttachment } from '@/features/chat/hooks/useChat';
import { useLatestCallback } from '@/common/hooks/useLatestCallback';
import { useNewChatGreeting } from '@/features/chat/hooks/useNewChatGreeting';
import { useChatCommands } from '@/features/chat/hooks/useChatCommands';
import { useSpeechRecognition } from '@/features/chat/hooks/useSpeechRecognition';
import { useChatSubmit } from '@/features/chat/hooks/useChatSubmit';
import { useMobileChatChrome } from '@/features/chat/hooks/useMobileChatChrome';
import { appendDictation } from './chatbox/speech-dictation';
import { useFreshActivePageContext } from './useFreshActivePageContext';
import { useFreshActiveDriveFolder } from './useFreshActiveDriveFolder';
import { useCrossModuleQuickActions } from './useCrossModuleQuickActions';
import { useOAuthConsentGate } from '@/features/chat/hooks/useOAuthConsentGate';
import { deriveLimitReachedState } from './limit-reached-state';
import { deriveQuotaBannerState } from './quota-banner-state';
import { apiGet } from '@/lib/api/client';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { useFeedback } from '@/app/providers/FeedbackContext';
import { useSession } from '@/features/auth/hooks/useSession';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { useConversationJson } from '@/features/conversations/hooks/useConversationJson';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import {
  handleViewIntent as runHandleViewIntent,
  handleOpenObjectIntent as runHandleOpenObjectIntent,
  handleFilterIntent as runHandleFilterIntent,
} from './chatbox/intent-handlers';

import type { ConversationPanel, WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { EditableModuleTitleRef } from '@/modules/_infrastructure/EditableModuleTitle';
import { MODULE_ALIASES, isSummaryModuleType, getModuleDisplayLabel, type Module } from '@/types/modules';
import { useEmailSyncOverlay } from '../hooks/useEmailSyncOverlay';
import { createIntegration, authorizeIntegration } from '@/lib/api/endpoints/integrations';
import { apiFetch } from '@/lib/apiClient';
import { useToast } from '@/app/providers/ToastContext';
import { useMarkAgentCreated } from '@/app/providers/AgentCreatedContext';
import {
  conversationEvents,
  dispatchWindowEvent,
} from '@/lib/conversation-events';
import { useAudioVisualizer } from '@/common/hooks/useAudioVisualizer';
import { useObjectSelection } from '@/app/providers/ObjectSelectionContext';
import { isNewChatPlaceholder } from '@/lib/greetings';
import { ChatConnectPrompt } from '@/features/chat/components/ChatConnectPrompt';
import { useGesture } from '@/features/voice/useGesture';
import { useDemoMode } from '@/features/demo/useDemoMode';
import { filterDemoQuickActions } from '@/features/chat/utils/demo-action-filter';
import { useVoiceMode } from '@/features/voice/useVoiceMode';
import { SetupScreenStyles } from '@/common/components/WorkspaceSetupScreen';
import '@/common/components/assistant-markdown.css';
import './ChatBox.css';
import { ChatComposer } from './ChatComposer';
import { MessageList } from './MessageList';
import { emailApiPath } from '@/lib/email-api'
import {
  ENABLE_EMBEDDED_MODULE_PANE_DEFAULT,
  EMBEDDED_MODULE_PANE_QUERY_KEY,
  EMBEDDED_MODULE_PANE_STORAGE_KEY,
  QUOTA_WARN_DISMISS_KEY,
} from './chatbox/constants';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { useOnboardingActive } from '@/app/providers/OnboardingActiveContext';
import { useReviseSession } from '@/app/providers/ReviseSessionContext';
import { useChatOnboarding } from '@/features/onboarding/useChatOnboarding';
import { resolveOnboardingRecipe, readOnboardingObParam, clearOnboardingObParam } from '@/features/onboarding/onboarding-recipes';
import { ChatDockFlight } from './chatbox/ChatDockFlight';
import { useMediaQuery } from '@/common/hooks/useMediaQuery';
import { BREAKPOINTS } from '@/lib/breakpoints';
import { useCommandAutocomplete } from '@/features/chat/hooks/useCommandAutocomplete';
import { useMentionAutocomplete } from '@/features/chat/hooks/useMentionAutocomplete';
import { useChatScroll } from '@/features/chat/hooks/useChatScroll';
import { useModulePaneState } from '@/features/chat/hooks/useModulePaneState';
import { useChatWindowEvents } from '@/features/chat/hooks/useChatWindowEvents';
import { useChatAttachments } from '@/features/chat/hooks/useChatAttachments';
import { useChatComposerInput } from '@/features/chat/hooks/useChatComposerInput';
import { useChatDockWiring } from '@/features/chat/hooks/useChatDockWiring';
import { useChatRenderState } from '@/features/chat/hooks/useChatRenderState';
import { useChatModulePaneActions } from '@/features/chat/hooks/useChatModulePaneActions';
import { useModulePaneEscapeClose } from '@/features/chat/hooks/useModulePaneEscapeClose';
import { useChatUiActions } from '@/features/chat/hooks/useChatUiActions';
import { useFinalCacheInvalidationOnStreamEnd } from '@/features/chat/hooks/useFinalCacheInvalidationOnStreamEnd';
import { flipPaneWithViewTransition } from './chatbox/pane-view-transition';
import { buildChatCallbacks } from './chatbox/chat-callbacks';
import { runModulePaneHydrationEffect, type PendingPaneHydration } from './chatbox/module-pane-hydration';
import { runModulePaneMissingClearEffect } from './chatbox/module-pane-missing-clear';
import { buildAvailableModules, resolveChatPaneModule } from './chatbox/module-pane-helpers';
import { buildBriefingContext } from '@/features/chat/lib/briefing';
import { isHttpPayload } from '@/features/chat/utils/http-payload-guard';
import {
  extractDisplayName,
  parseBooleanFlag,
} from '@/features/chat/utils/chat-text';
import type {
  UsageQuotaResponse,
  PendingAttachment,
  PastedAttachment,
  ChatBoxProps,
} from './chatbox/types';
import { ChatPaneCard } from './chatbox/ChatPaneCard';
import { ChatModalsLayer } from './chatbox/ChatModalsLayer';
import { ChatCsvImportModal } from './chatbox/ChatCsvImportModal';
import { RESULT_CARDS_MARKER } from './result-cards/types';

// Handle interface for parent components to call methods
export type ChatBoxHandle = {
  clearChat: () => void;
  syncMode: () => void;
  addAssistantMessage: (content: string, conversationId?: string) => void;
  sendMessage: (content: string, conversationId?: string) => Promise<void>;
};

// Module-level set so it survives component remounts (React StrictMode double-invoke, profile reload).
// Resets on page refresh, which is the desired behavior.
const greetedConversationIds = new Set<string>();

const ChatBox = forwardRef<ChatBoxHandle, ChatBoxProps>(({ isMobile = false }, ref) => {
  const { activeConversation, allConversations: conversationContextConversations, deleteConversation, pluginInstances, lastCreatedConversationId, lastCreatedConversationOriginId } = useConversation();
  const allConversations = conversationContextConversations ?? [];

  // Block chat if no conversations exist at all (must create one first)
  const hasNoConversations = allConversations.length === 0;
  const { openModal } = useFeedback();
  const { token, isReadOnly, profile, selectedContainer, updateProfile, refresh: refreshSession } = useSession();
  const workspaceId = useMemo(() => resolveWorkspaceId(selectedContainer), [selectedContainer]);
  const { selectObject } = useObjectSelection();

  // Copy feedback for message action buttons (#1697)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { conversationId: routeConversationIdParam, moduleId: moduleIdParam, view: routeViewParam } = useParams<{
    conversationId?: string;
    moduleId?: string;
    view?: string;
  }>();
  const requestedRouteModuleId = useMemo(() => {
    const queryModuleId = new URLSearchParams(location.search).get('moduleId');
    const rawModuleId = moduleIdParam ?? queryModuleId;
    if (!rawModuleId) return null;
    try {
      return decodeURIComponent(rawModuleId);
    } catch {
      return rawModuleId;
    }
  }, [location.search, moduleIdParam]);
  const isEmbeddedModulePaneEnabled = useMemo(() => {
    if (typeof window === 'undefined') return ENABLE_EMBEDDED_MODULE_PANE_DEFAULT;

    const queryValue = new URLSearchParams(location.search).get(EMBEDDED_MODULE_PANE_QUERY_KEY);
    const queryOverride = parseBooleanFlag(queryValue);
    if (queryOverride !== null) return queryOverride;

    try {
      const storedValue = window.localStorage.getItem(EMBEDDED_MODULE_PANE_STORAGE_KEY);
      const storageOverride = parseBooleanFlag(storedValue);
      if (storageOverride !== null) return storageOverride;
    } catch {
      // Ignore storage access failures and use default.
    }

    return ENABLE_EMBEDDED_MODULE_PANE_DEFAULT;
  }, [location.search]);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const markCreated = useMarkAgentCreated();
  const chatBoxRootRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const modulePaneCardRef = useRef<HTMLDivElement | null>(null);
  const modulePaneTitleRef = useRef<EditableModuleTitleRef>(null);
  const latestMessageIdRef = useRef<string | null>(null);
  const wasModulePaneSplitRef = useRef(false);
  const moduleEmbedAnchorByConversationRef = useRef<Record<string, string | null>>({});
  const previousConversationIdRef = useRef<string | null>(null);
  const userHasScrolledRef = useRef(false); // Track if user has scrolled up at all
  const isInitialLoadRef = useRef(true); // Track first load for auto-scroll
  const [isScrollSwitchSettling, setIsScrollSwitchSettling] = useState(false);
  const scrollSwitchSettlingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoTitleRefreshTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  // Follow-up invalidation timers for slow server-side post-processing
  // (e.g. place auto-enrich uploading photos to R2). Tracked so we can cancel
  // when the user navigates away — otherwise they'd fire against the wrong
  // conversation context for up to a minute after stream end. #2458
  const postStreamEnrichmentTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const forceBottomRef = useRef<{
    conversationId: string | null;
    active: boolean;
    startedAtMs: number;
  }>({
    conversationId: null,
    active: false,
    startedAtMs: 0,
  });
  const messageQueueRef = useRef<Array<{ content: string; conversationId: string; context?: any; images?: Array<{ mediaType: string; data: string; filename: string }>; documents?: Array<{ mediaType: string; data: string; filename: string }>; attachments?: ChatAttachment[]; pastedAttachments?: Array<{ content: string; label: string; size: number }> }>>([]); // Queue messages while streaming
  const nameInputRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const bottomStackRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track whether we've sent conversation context to the current model in this session.
  // We only need to send it once per conversation + model selection.
  const conversationContextSentRef = useRef<string | null>(null);


  // Deterministic Query Engine: preload conversation data for instant responses
  const { data: conversationJson } = useConversationJson(activeConversation?.id ?? null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [userName, setUserName] = useState('You');

  // Sync userName with profile.name when it changes
  useEffect(() => {
    if (profile?.name) {
      setUserName(profile.name);
    }
  }, [profile?.name]);

  // Configuration - hardcoded to API mode (bridge picks its own model)
  type LLMMode = 'local' | 'api';
  const mode: LLMMode = 'api';
  const specificModel: string | undefined = undefined;

  const [compactMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('chat-compact-mode') === 'true';
    }
    return false;
  });

  // Reset context tracking and scroll behavior when conversation changes.
  useEffect(() => {
    if (autoTitleRefreshTimersRef.current.length > 0) {
      for (const timer of autoTitleRefreshTimersRef.current) {
        clearTimeout(timer);
      }
      autoTitleRefreshTimersRef.current = [];
    }
    if (postStreamEnrichmentTimersRef.current.length > 0) {
      for (const timer of postStreamEnrichmentTimersRef.current) {
        clearTimeout(timer);
      }
      postStreamEnrichmentTimersRef.current = [];
    }

    if (scrollSwitchSettlingTimerRef.current) {
      clearTimeout(scrollSwitchSettlingTimerRef.current);
      scrollSwitchSettlingTimerRef.current = null;
    }

    if (activeConversation?.id) {
      // New conversation - reset so we send context on first message
      conversationContextSentRef.current = null;
      setIsScrollSwitchSettling(true);
      // Safety valve: never keep message area hidden for long.
      scrollSwitchSettlingTimerRef.current = setTimeout(() => {
        setIsScrollSwitchSettling(false);
        scrollSwitchSettlingTimerRef.current = null;
      }, 100);
    } else {
      forceBottomRef.current = { conversationId: null, active: false, startedAtMs: 0 };
      setIsScrollSwitchSettling(false);
    }
    isInitialLoadRef.current = true;
    userHasScrolledRef.current = false;
  }, [activeConversation?.id]);

  useEffect(() => {
    return () => {
      if (autoTitleRefreshTimersRef.current.length > 0) {
        for (const timer of autoTitleRefreshTimersRef.current) {
          clearTimeout(timer);
        }
        autoTitleRefreshTimersRef.current = [];
      }
      if (postStreamEnrichmentTimersRef.current.length > 0) {
        for (const timer of postStreamEnrichmentTimersRef.current) {
          clearTimeout(timer);
        }
        postStreamEnrichmentTimersRef.current = [];
      }
      if (scrollSwitchSettlingTimerRef.current) {
        clearTimeout(scrollSwitchSettlingTimerRef.current);
        scrollSwitchSettlingTimerRef.current = null;
      }
    };
  }, []);

  // Voice input state
  const [inputValue, setInputValue] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  // Large plain-text pastes held as collapsed chips — see PASTE_ATTACHMENT_THRESHOLD_BYTES.
  const [pastedAttachments, setPastedAttachments] = useState<PastedAttachment[]>([]);
  const [expandedPastedAttachmentIds, setExpandedPastedAttachmentIds] = useState<Set<string>>(new Set());
  const [selectedEditSections, setSelectedEditSections] = useState<Array<{ sectionId: string; label: string }>>([]);
  const lastSentRef = useRef<{ text: string; at: number } | null>(null);
  // Transient cache of files the user just sent through the composer,
  // keyed by lowercase filename. Used by the contacts.import-csv quick
  // action: the agent emits a chip after the user uploads a CSV, the
  // chip click looks up the original File here and routes it to the
  // ImportCSVModal. Capped at the last 8 entries to avoid leaks.
  const recentlySentFilesRef = useRef<Map<string, File>>(new Map());

  // Chat-driven CSV import — opens ImportCSVModal at the chat level
  // (not module-scoped) so it works regardless of which modules are
  // attached to the conversation. Contacts are workspace-level objects;
  // the modal just POSTs to /bulk/objects.
  const [chatImportCSVFile, setChatImportCSVFile] = useState<File | null>(null);

  // OAuth consent interstitial (#2703 follow-up). Shown BEFORE
  // connectProvider redirects to Google's consent screen, so the user sees
  // exactly what Xavior will and won't do (read-only, no send) without
  // having to read the Google scope list.
  const { pendingOAuthConsent, setPendingOAuthConsent, confirmOAuthConsent } =
    useOAuthConsentGate();

  // Audio waveform visualizer for dictation mode
  const { analyser, start: startAudioViz, stop: stopAudioViz } = useAudioVisualizer();

  // Web Speech API dictation cluster (recognitionRef/isListeningRef/isListening
  // + init effect + start/stop primitives) extracted to useSpeechRecognition.
  // Bodies preserved verbatim there; this single hook call owns that state and
  // exposes the refs/setters the voice-mode interplay below still reads/writes.
  const {
    isListening,
    interimTranscript,
    setInterimTranscript,
    toggleVoiceInput,
    stopDictation,
  } = useSpeechRecognition({
    setInputValue,
    chatInputRef,
    startAudioViz,
    stopAudioViz,
  });

  // Listen for section-selection events from PageRenderer inspect mode
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (Array.isArray(detail?.sections)) {
        setSelectedEditSections(detail.sections as Array<{ sectionId: string; label: string }>);
      }
    };
    window.addEventListener('xavior:edit-sections-changed', handler);
    return () => window.removeEventListener('xavior:edit-sections-changed', handler);
  }, []);

  // Command autocomplete state
  const {
    showCommandDropdown,
    setShowCommandDropdown,
    commandMatches,
    setCommandMatches,
    selectedCommandIndex,
    setSelectedCommandIndex,
  } = useCommandAutocomplete();

  // Pending module selection state (when multiple modules match an object type)
  interface PendingModuleSelection {
    objectType: 'task' | 'note' | 'contact' | 'event';
    parsedData: any;
    modules: Array<{ id: string; label: string }>;
    originalInput: string;
  }
  const [pendingModuleSelection, setPendingModuleSelection] = useState<PendingModuleSelection | null>(null);

  // Use real chat hook with SSE streaming
  const sessionId = activeConversation?.id;
  const baseConversationModules = useMemo(
    () =>
      (activeConversation?.modules || [])
        .filter((module) => !module.archived && !isSummaryModuleType(module.type))
        .slice()
        .sort((a, b) => a.position - b.position),
    [activeConversation?.modules],
  );
  const activeModule = useMemo(() => {
    const modules = baseConversationModules;

    if (modules.length === 0) return null;

    const moduleId = requestedRouteModuleId;
    if (moduleId) {
      if (moduleId.startsWith('plugin:')) {
        return null;
      }
      const match = modules.find((module) => module.id === moduleId);
      if (match) return match;
      // A SPECIFIC module was requested via the URL but isn't in the list yet —
      // the modules list is lagging (refresh/race) or the module was filtered out.
      // Render nothing rather than flashing the WRONG (first) module; it resolves
      // once the list catches up. (MainLayout redirects genuinely-invalid IDs.)
      return null;
    }

    // No specific module requested → default to the first module.
    return modules[0] || null;
  }, [baseConversationModules, requestedRouteModuleId]);

  // For plugin modules (e.g., Gmail), extract instanceId and pluginId for agent context
  const activePluginContext = useMemo(() => {
    const moduleId = requestedRouteModuleId;
    if (!moduleId || !moduleId.startsWith('plugin:')) return null;
    const instanceId = moduleId.replace('plugin:', '');
    const instance = pluginInstances.find(pi => pi.id === instanceId);
    if (!instance) return null;
    return { id: instanceId, pluginId: instance.pluginId, label: instance.pluginId };
  }, [requestedRouteModuleId, pluginInstances]);

  const activeModuleObjectType = useMemo(() => {
    if (activeModule) {
      if (activeModule.objectType) return activeModule.objectType;
      if (activeModule.objectTypes && activeModule.objectTypes.length > 0) {
        return activeModule.objectTypes[0];
      }

      const typeFallbacks: Record<string, string> = {
        task_list: 'task',
        kanban: 'task',
        contact_list: 'contact',
        client_list: 'contact',
        crm: 'contact',
        people_list: 'contact',
        note_grid: 'note',
        event_list: 'event',
        calendar: 'event',
        timeline: 'event',
        place_list: 'place',
        place_card: 'place',
        place_detail: 'place',
        drive: 'asset',
        file_viewer: 'asset',
      };

      return typeFallbacks[activeModule.type] ?? undefined;
    }
    // For plugin modules, use the pluginId as the object type (e.g., "email")
    if (activePluginContext) return activePluginContext.pluginId;
    return undefined;
  }, [activeModule, activePluginContext]);

  const activeModuleId = activeModule?.id ?? activePluginContext?.id;
  // Stale pre-rename default labels (e.g. task_list's old "Tasks") resolve to
  // the catalog default so the agent context matches what the UI displays.
  const activeModuleLabel = activeModule ? getModuleDisplayLabel(activeModule) : activePluginContext?.label;
  const [panelBoundModules, setPanelBoundModules] = useState<Module[]>([]);
  const [workspaceModuleLabels, setWorkspaceModuleLabels] = useState<string[]>([]);
  // Panel-bound modules linger on the previous conversation until their async
  // refresh lands, so buildAvailableModules filters them by conversation_id to
  // keep availableModules from transiently mixing two conversations on a switch
  // (#5188). Base modules are always the active conversation's own set.
  const availableModules = useMemo(
    () => buildAvailableModules(baseConversationModules, panelBoundModules, activeConversation?.id ?? null),
    [activeConversation?.id, baseConversationModules, panelBoundModules],
  );
  const availableModulesRef = useRef(availableModules);
  availableModulesRef.current = availableModules;
  // Pane state lives in ModulePaneContext — the single source of truth for
  // "which module is open" (ADR 028). The provider seeds it from the URL on
  // first mount (pane renders on first paint for deep links; mobile seeds
  // closed), SidebarRail highlights from it, and this component renders from
  // it. Intent-tagged writes let the provider discard background hydration
  // that races a user click. Local aliases keep the names the rest of this
  // file (and the extracted hooks) were written against.
  const {
    paneModuleId: chatPaneModuleId,
    isPaneVisible: isModulePaneVisible,
    setPaneModuleId,
    setPaneVisible,
    setRenderedModuleId,
    beginHydration: beginPaneHydration,
    isHydrationCurrent: isPaneHydrationCurrent,
    registerPaneFlipWrapper,
  } = useModulePane();
  // User/agent-intent writers (focusModule, hideModulePane, clearChat, modals).
  const setChatPaneModuleId = useCallback(
    (value: SetStateAction<string | null>) => setPaneModuleId(value, { intent: true }),
    [setPaneModuleId],
  );
  // Background writers (hydration seed/apply, missing-module auto-cleanup) —
  // must never invalidate an in-flight hydration or outrank a user click.
  const setChatPaneModuleIdBackground = useCallback(
    (value: SetStateAction<string | null>) => setPaneModuleId(value, { intent: false }),
    [setPaneModuleId],
  );
  // NOTE: writes go through setIsModulePaneVisible / …WithoutFlight (defined
  // with the pane-flight wiring below) so user-intent flips can stage the
  // open/close flight while hydration/auto-cleanup flips stay instant.
  // Hides the chat side of the split, giving the module pane the full width
  // ("chat docks to header"). Owned by ChatDockContext — shared with the
  // header chat launcher in MainLayout — and persisted across sessions.
  // Ignored on mobile (mobile is already single-pane).
  // 'docked' applies the display:none collapse; 'docking'/'undocking' are the
  // transitional flight phases of the shared-element morph.
  const {
    dockState: chatDockState,
    dockChat,
    finishUndock,
    expandChatPane,
    setSplitViewActive: setChatDockSplitViewActive,
    setModulePaneOpen,
    pendingOpenFocus,
    ackOpenFocus,
    pendingLandingReveal,
    ackLandingReveal,
    flightRect: chatDockFlightRect,
    registerUndockRectProvider,
  } = useChatDock();
  const isChatPaneCollapsed = chatDockState === 'docked';
  const isDesktopSplitViewport = useMediaQuery(`(min-width: ${BREAKPOINTS.desktopSplitMin}px)`);
  // Dock callbacks + effects (measureChatColumnRect/handleDockChat + the
  // register/landing effects) moved to useChatDockWiring below — see that hook
  // call near the pre-return block, where the rest of the dock cluster lives.
  const [objectCardPopover, setObjectCardPopover] = useState<{ objectType: string; objectId: string } | null>(null);
  // A note pulled up from a chat card — shown as plain text in NoteCardPopover.
  const [noteModal, setNoteModal] = useState<{ noteId: string } | null>(null);
  // A doc pulled up from a chat card — opens DocEditorModal directly (no Drive module focus).
  const [docModal, setDocModal] = useState<{ docId: string } | null>(null);
  const [showIntegrationAccountsModal, setShowIntegrationAccountsModal] = useState(false);
  const [showManageAccountsModal, setShowManageAccountsModal] = useState(false);
  const [mobileView, setMobileView] = useState<'chat' | 'module'>('chat');
  const [moduleEmbedAnchorMessageId, setModuleEmbedAnchorMessageId] = useState<string | null>(null);
  const [archivedInlineModuleIds, setArchivedInlineModuleIds] = useState<Set<string>>(new Set());
  const conversationPanelsCacheRef = useRef<Record<string, ConversationPanel[]>>({});
  const modulePaneHydrationSequenceRef = useRef(0);
  // Not-yet-applied hydration read (set/cleared by the hydration runner) — a
  // same-conversation effect re-run adopts it instead of dropping the restore.
  const pendingHydrationRef = useRef<PendingPaneHydration | null>(null);
  const activeConversationIdRef = useRef<string | null>(activeConversation?.id ?? null);
  // Last REAL module the pane resolved — the bridge source for resolveChatPaneModule
  // (both the still-loading window and the within-conversation module switch). See
  // that helper for the exact windows it bridges (and the ones it deliberately does
  // not: conversation switch, deleted module).
  const lastSelectedModuleRef = useRef<Module | null>(null);
  // `resolved` = the REAL resolution (target present in availableModules) —
  // source of truth for cleanup. `rendered` = what to show, bridging to the last
  // rendered module while a just-switched target hasn't attached yet, so the
  // split grid never collapses mid-view-transition. See resolveChatPaneModule.
  const { resolved: resolvedChatPaneModule, rendered: selectedChatPaneModule } = useMemo(
    () => resolveChatPaneModule({
      chatPaneModuleId,
      availableModules,
      isPaneVisible: isModulePaneVisible,
      lastRenderedModule: lastSelectedModuleRef.current,
    }),
    [availableModules, chatPaneModuleId, isModulePaneVisible],
  );
  // Track the last REAL module the pane resolved (never the bridge), so the
  // bridge always points at a genuine previous module, not itself.
  if (resolvedChatPaneModule) lastSelectedModuleRef.current = resolvedChatPaneModule;

  // Gmail status for the chat-pane module (gates Sync menu item). Only queries when the
  // active pane module is a Gmail module and we have a token.
  const chatPaneModuleCanonicalType = selectedChatPaneModule
    ? (MODULE_ALIASES[selectedChatPaneModule.type] ?? selectedChatPaneModule.type)
    : null;
  const { data: chatPaneEmailStatus } = useQuery<{ connected?: boolean; accounts?: Array<{ id: string }> }>({
    queryKey: ['email-status-module', selectedChatPaneModule?.id ?? ''],
    queryFn: () => apiFetch(emailApiPath() + '/status', {
      searchParams: { moduleId: selectedChatPaneModule!.id },
      authToken: token ?? undefined,
    }),
    enabled: chatPaneModuleCanonicalType === 'email' && Boolean(selectedChatPaneModule?.id) && Boolean(token),
    staleTime: 30_000,
  });
  const chatPaneHasEmailAccount = (chatPaneEmailStatus?.accounts?.length ?? 0) > 0;

  // Soft-warn quota banner — polls workspace quota on chat mount and is invalidated
  // after each chat-turn `onStreamEnd` so the banner reflects current usage.
  // Errors are swallowed (banner simply does not appear) — informational UI.
  const { data: quota } = useQuery<UsageQuotaResponse | null>({
    queryKey: ['workspace', 'quota'],
    queryFn: async () => {
      const wsId = getActiveWorkspaceId();
      const res = await apiGet<UsageQuotaResponse>(`/workspaces/${wsId}/quota`);
      return res.data;
    },
    staleTime: 30_000,
    enabled: Boolean(token),
    retry: 1,
  });

  // Per-tab session dismiss for the warn banner (sessionStorage, not local).
  // Re-shows on a fresh tab/session, matching OpenInAppBanner's pattern.
  const [quotaWarnDismissed, setQuotaWarnDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(QUOTA_WARN_DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const {
    show: showQuotaWarnBanner,
    pct: quotaWarnPct,
    window: quotaWarnWindow,
  } = deriveQuotaBannerState(quota ?? null, quotaWarnDismissed);

  // Gmail sync-in-progress tracking: keep the options menu open while sync is
  // running, show a spinner on the Sync item, and close the menu when sync
  // completes. Driven by events dispatched from EmailPanel.handleSyncNow.
  const { moduleMenuCloseSignal, isChatPaneEmailSyncing } = useEmailSyncOverlay(selectedChatPaneModule);
  // Effective active module: chat pane module takes priority when visible
  const effectiveActiveModuleId = (isModulePaneVisible && chatPaneModuleId)
    ? chatPaneModuleId
    : activeModuleId;
  const effectiveActiveModuleLabel = (isModulePaneVisible && selectedChatPaneModule)
    ? getModuleDisplayLabel(selectedChatPaneModule)
    : activeModuleLabel;
  const effectiveActiveModuleObjectType = useMemo(() => {
    if (isModulePaneVisible && selectedChatPaneModule) {
      if ((selectedChatPaneModule as any).objectType) return (selectedChatPaneModule as any).objectType;
      if ((selectedChatPaneModule as any).objectTypes?.length) return (selectedChatPaneModule as any).objectTypes[0];
      const typeFallbacks: Record<string, string> = {
        task_list: 'task', kanban: 'task', contact_list: 'contact', client_list: 'contact', crm: 'contact',
        people_list: 'contact', note_grid: 'note', event_list: 'event',
        calendar: 'event', timeline: 'event', place_list: 'place', place_card: 'place',
        place_detail: 'place', drive: 'asset', file_viewer: 'asset', email: 'email',
      };
      return typeFallbacks[selectedChatPaneModule.type] ?? undefined;
    }
    return activeModuleObjectType;
  }, [isModulePaneVisible, selectedChatPaneModule, activeModuleObjectType]);
  // Track active page context from PagesContainerModule.
  // useFreshActivePageContext maintains BOTH React state (for rendering) AND
  // a ref updated synchronously in the event handler (for send-time reads).
  // Passing the ref to useChat closes the staleness race: if the event fires
  // just before the user hits Enter, the ref already holds the latest value
  // even though React hasn't flushed the state update through a re-render yet.
  const {
    state: activePageContext,
    ref: activePageContextRef,
  } = useFreshActivePageContext();
  const { state: activeDriveFolder, ref: activeDriveFolderRef } = useFreshActiveDriveFolder();

  // Track active object context (e.g., which contact/event the user is viewing)
  const [activeObjectContext, setActiveObjectContext] = useState<{objectType: string; objectId: string; name: string; email?: string; summary?: string} | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      setActiveObjectContext((e as CustomEvent).detail ?? null);
    };
    window.addEventListener('xavior:active-object-changed', handler);
    return () => window.removeEventListener('xavior:active-object-changed', handler);
  }, []);

  // Clear section selections + deactivate inspect when the CONVERSATION changes
  // (NOT on page switches within a conversation — that flickered focus chips via
  // PageRenderer's inspector-on-default effect). Conversation switch is the real
  // clean-slate boundary; same-conversation page switches preserve inspect mode.
  useEffect(() => {
    setSelectedEditSections([]);
    // Also deactivate inspect mode in the iframe
    window.dispatchEvent(new CustomEvent('xavior:inspect-off'));
  }, [activeConversation?.id]);

  const shouldShowModulePane = isModulePaneVisible
    && Boolean(selectedChatPaneModule)
    && Boolean(activeConversation?.id);
  const shouldShowAnyPane = shouldShowModulePane;
  const isMobileModuleView = isMobile && shouldShowModulePane && mobileView === 'module';
  const isMobileChatWithModule = isMobile && shouldShowModulePane && mobileView === 'chat';
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Module-pane open/close animation: a browser View Transition. The browser
  // snapshots the REAL pixels of the named elements (chat messages, composer,
  // module card — see the view-transition-name rules in ChatBox.css), the
  // layout flips underneath, and the old image morphs into the new live
  // render — the user keeps seeing their actual chat content the whole way.
  // Gates read through a render-refreshed ref so the setter identity stays
  // stable for the long-lived callbacks that capture it.
  const paneFlipGatesRef = useRef({ isMobile, isDesktopSplitViewport, prefersReducedMotion, chatDockState });
  paneFlipGatesRef.current = { isMobile, isDesktopSplitViewport, prefersReducedMotion, chatDockState };
  // User-intent visibility writes animate (desktop split, motion allowed,
  // dock resting — 'open' for normal open/close, 'docked' so the
  // docked-module exit morphs too); hydration / conversation-switch /
  // auto-cleanup writes flip instantly via the Instant variant.
  // The provider updates the authority mirror + intent sequence SYNCHRONOUSLY
  // and skips same-value flips itself; `defer: true` routes only the React
  // render flip through this wrapper (startViewTransition runs its update
  // callback async — the intent must never ride it).
  const paneFlip = useCallback((apply: () => void, visible: boolean) => {
    const gates = paneFlipGatesRef.current;
    const animate = !gates.isMobile
      && gates.isDesktopSplitViewport
      && !gates.prefersReducedMotion
      && (gates.chatDockState === 'open' || gates.chatDockState === 'docked');
    // Closing slides the pane out to the left; opening keeps the default morph.
    // BOTH directions tag <html> so CSS can freeze the outer layout grids for
    // the flip's duration (MainLayout.css / DailySummaryModal.css): the morph
    // supplies ALL the motion, so the grids must snap to their destination or
    // the snapshot captures mid-transition geometry and the Brief+chat column
    // rises from the bottom (open) / shears apart (close) instead of gliding.
    flipPaneWithViewTransition(apply, animate, visible ? 'vt-pane-opening' : 'vt-pane-closing');
  }, []);
  const setIsModulePaneVisible = useCallback((visible: boolean) => {
    setPaneVisible(visible, { intent: true, defer: true });
  }, [setPaneVisible]);
  const setIsModulePaneVisibleWithoutFlight = useCallback((visible: boolean) => {
    setPaneVisible(visible, { intent: false });
  }, [setPaneVisible]);
  // Hand the flight to the provider so SidebarRail's direct openPane (the
  // user-intent open recorded BEFORE any event/network hop) animates exactly
  // like an in-ChatBox open.
  useEffect(() => {
    registerPaneFlipWrapper(paneFlip);
    return () => registerPaneFlipWrapper(null);
  }, [registerPaneFlipWrapper, paneFlip]);
  // Publish what the pane actually has on screen. SidebarRail's toggle-close
  // only fires against a CONFIRMED open pane — an unconfirmed highlight can
  // then never turn a click into a silent no-op close.
  useEffect(() => {
    setRenderedModuleId(shouldShowModulePane ? (selectedChatPaneModule?.id ?? null) : null);
  }, [setRenderedModuleId, shouldShowModulePane, selectedChatPaneModule?.id]);
  useEffect(() => () => {
    // ChatBox can unmount with the pane open (conversation deleted/cleared) —
    // a stale confirmation would let the rail close a pane that no longer exists.
    setRenderedModuleId(null);
  }, [setRenderedModuleId]);
  const clickableModuleLabels = useMemo(() => {
    const labels = new Set<string>();
    for (const label of workspaceModuleLabels) {
      const trimmed = label.trim();
      if (trimmed) labels.add(trimmed);
    }
    for (const module of availableModules) {
      const trimmed = (module.label || '').trim();
      if (trimmed) labels.add(trimmed);
    }
    return Array.from(labels);
  }, [availableModules, workspaceModuleLabels]);

  // Timer ref for debounced tool-activity invalidation
  const toolInvalidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAttachedModuleFocusQueueRef = useRef<Array<{
    conversationId: string;
    moduleId: string;
    anchorMessageId: string | null;
    sequence: number;
  }>>([]);
  const moduleAttachInFlightRef = useRef<Map<string, Promise<boolean>>>(new Map());
  const workspaceModuleCatalogRef = useRef<WorkspaceModule[]>([]);
  const moduleFocusSequenceRef = useRef(0);
  const lastAppliedModuleFocusSequenceRef = useRef(0);
  const modulePaneMissingClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localViewIntentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewIntentPhraseIndexRef = useRef(0);
  const [isLocalViewIntentThinking, setIsLocalViewIntentThinking] = useState(false);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (toolInvalidationTimerRef.current) {
        clearTimeout(toolInvalidationTimerRef.current);
      }
      if (modulePaneMissingClearTimerRef.current) {
        clearTimeout(modulePaneMissingClearTimerRef.current);
        modulePaneMissingClearTimerRef.current = null;
      }
      if (localViewIntentTimerRef.current) {
        clearTimeout(localViewIntentTimerRef.current);
      }
    };
  }, []);

  // Module-pane persistence/catalog callbacks + effects (contiguous hook slice).
  // Extracted verbatim to hooks/useModulePaneState.ts — same callbacks, same
  // dep arrays, same effect bodies, same relative hook order. panelCreateInflightRef
  // now lives inside the hook. Refs/setters are threaded in by reference.
  const {
    refreshWorkspaceModuleCatalog,
    refreshPanelBoundModules,
    persistModulePaneState,
    hydrateModulePaneState,
    ensureModulePanelInConversationOnce,
    queuePendingModuleFocus,
  } = useModulePaneState({
    token,
    workspaceId,
    activeConversation,
    conversationPanelsCacheRef,
    workspaceModuleCatalogRef,
    activeConversationIdRef,
    moduleAttachInFlightRef,
    pendingAttachedModuleFocusQueueRef,
    setWorkspaceModuleLabels,
    setPanelBoundModules,
  });

  useEffect(() => {
    activeConversationIdRef.current = activeConversation?.id ?? null;
  }, [activeConversation?.id]);

  // Conversation-change module-pane hydration (incl. the hydration-sequence race
  // guard). Body extracted to chatbox/module-pane-hydration.ts; this useEffect
  // keeps its EXACT dep array and returns the runner's cleanup. All writes are
  // BACKGROUND (intent:false) and the async apply is additionally gated on the
  // provider's intent guard — a user click landing during the persisted-state
  // GET can never be overwritten by its result (ADR 028).
  useEffect(() => {
    return runModulePaneHydrationEffect({
      conversationId: activeConversation?.id ?? null,
      isMobile,
      moduleIdParam: moduleIdParam ?? null,
      view: routeViewParam ?? null,
      moduleEmbedAnchorMessageId,
      previousConversationIdRef,
      moduleEmbedAnchorByConversationRef,
      modulePaneHydrationSequenceRef,
      pendingHydrationRef,
      latestMessageIdRef,
      setChatPaneModuleId: setChatPaneModuleIdBackground,
      // Conversation-switch flips are not user gestures — route them through
      // the wrapper so the pane glide is skipped (instant, as before).
      setIsModulePaneVisible: setIsModulePaneVisibleWithoutFlight,
      setMobileView,
      setModuleEmbedAnchorMessageId,
      hydrateModulePaneState,
      beginHydration: beginPaneHydration,
      isHydrationCurrent: isPaneHydrationCurrent,
    });
  }, [activeConversation?.id, hydrateModulePaneState, beginPaneHydration, isPaneHydrationCurrent]);

  // Clears the pane when its module vanishes (after an 1800ms grace). Body
  // extracted verbatim to chatbox/module-pane-missing-clear.ts; this useEffect
  // keeps its EXACT dep array, and the timer ref + setters are threaded by ref.
  useEffect(() => {
    runModulePaneMissingClearEffect({
      activeConversationId: activeConversation?.id,
      chatPaneModuleId,
      isModulePaneVisible,
      availableModules,
      // Validate against the REAL resolution, never the bridged render value —
      // otherwise the bridge would keep this non-null and mask genuine deletions.
      selectedChatPaneModule: resolvedChatPaneModule,
      modulePaneMissingClearTimerRef,
      // Auto-cleanup is not a user gesture — background write, no pane flight.
      setChatPaneModuleId: setChatPaneModuleIdBackground,
      setIsModulePaneVisible: setIsModulePaneVisibleWithoutFlight,
      setModuleEmbedAnchorMessageId,
    });
  }, [activeConversation?.id, availableModules, chatPaneModuleId, isModulePaneVisible, resolvedChatPaneModule]);

  useEffect(() => {
    if (!activeConversation?.id || !chatPaneModuleId || !isModulePaneVisible) return;
    // Ensure sidebar module tracking updates for any chat-pane open path,
    // including hydrated state and non-command focus transitions.
    dispatchWindowEvent(conversationEvents.moduleFocused, {
      conversationId: activeConversation.id,
      moduleId: chatPaneModuleId,
    });
  }, [activeConversation?.id, chatPaneModuleId, isModulePaneVisible]);

  useEffect(() => {
    if (!isModulePaneVisible) {
      setModuleEmbedAnchorMessageId(null);
    }
  }, [isModulePaneVisible]);

  // Reset action state on conversation changes
  useEffect(() => {
    pendingAttachedModuleFocusQueueRef.current = [];
    moduleAttachInFlightRef.current.clear();
    moduleFocusSequenceRef.current = 0;
    lastAppliedModuleFocusSequenceRef.current = 0;
    if (localViewIntentTimerRef.current) {
      clearTimeout(localViewIntentTimerRef.current);
      localViewIntentTimerRef.current = null;
    }
    setIsLocalViewIntentThinking(false);
  }, [activeConversation?.id]);

  // Helper: invalidate conversation structure (modules/panels/lists)
  const invalidateConversationStructure = useCallback((conversationId: string) => {
    delete conversationPanelsCacheRef.current[conversationId];
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.detail(conversationId),
      refetchType: 'all',
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.lists(),
      refetchType: 'all',
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.all,
      refetchType: 'all',
    });
  }, [queryClient]);

  const scheduleAutoTitleFollowupRefresh = useCallback(
    (conversationId: string, conversationName: string | null | undefined) => {
      if (autoTitleRefreshTimersRef.current.length > 0) {
        for (const timer of autoTitleRefreshTimersRef.current) {
          clearTimeout(timer);
        }
        autoTitleRefreshTimersRef.current = [];
      }
      if (!isNewChatPlaceholder(conversationName)) return;

      const refreshDelaysMs = [900, 1900, 3400, 5400];
      for (const delayMs of refreshDelaysMs) {
        const timer = setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.lists(),
            refetchType: 'all',
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.detail(conversationId),
            refetchType: 'all',
          });
        }, delayMs);
        autoTitleRefreshTimersRef.current.push(timer);
      }
    },
    [queryClient],
  );

  // Helper: invalidate object data (safe while streaming)
  // Uses type-scoped keys (no conversationId) to match module-scoped cache structure.
  // refetchType:'all' ensures unobserved module queries also refetch — without it,
  // tanstack-query's default ('active') only refetches currently-mounted modules,
  // so agent-created objects wouldn't appear when the user later opens the module
  // until a page refresh forced a fresh query (issue #2096).
  const invalidateConversationObjects = useCallback((_conversationId?: string) => {
    const objectTypes = ['task', 'contact', 'event', 'place', 'note', 'asset', 'stat'];
    objectTypes.forEach((type) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.byType(type),
        refetchType: 'all',
      });
    });
  }, [queryClient]);

  // Module-pane focus/persist action sub-layer (dispatchModuleCommand, focusModule
  // + focusModuleRef, hideModulePane, the pending-attached-module focus-apply
  // effect) extracted verbatim to hooks/useChatModulePaneActions.ts — same bodies,
  // same byte-identical dep arrays, same relative hook order (this stays before
  // useChat). Only the pieces NOT entangled with later-declared hooks were lifted;
  // applyUiActions/onCardAction/openWorkspaceModuleByLabel (which close over the
  // later `onboarding` + useChat outputs) stay below. focusModuleRef is created in
  // the hook and returned so applyUiActions / route-focus effects read the same
  // stable identity. Refs + setters thread in by reference.
  const {
    dispatchModuleCommand,
    focusModuleRef,
    hideModulePane,
  } = useChatModulePaneActions({
    activeConversation,
    isMobile,
    isEmbeddedModulePaneEnabled,
    isModulePaneVisible,
    chatPaneModuleId,
    selectedChatPaneModule,
    availableModules,
    persistModulePaneState,
    setModuleEmbedAnchorMessageId,
    setIsModulePaneVisible,
    setChatPaneModuleId,
    setMobileView,
    latestMessageIdRef,
    moduleEmbedAnchorByConversationRef,
    modulePaneCardRef,
    wasModulePaneSplitRef,
    pendingAttachedModuleFocusQueueRef,
    lastAppliedModuleFocusSequenceRef,
  });

  // UI-action cluster (applyUiActions + onCardAction + openWorkspaceModuleByLabel
  // + the apply-ui-actions / route-module-focus effects) extracted VERBATIM to
  // hooks/useChatUiActions.ts. The `applyUiActionsRef` the hook exposes is no
  // longer read by ChatBox, so it stays internal to the hook. The legacy
  // onboarding manager/directive handle was removed (recipe-replacement, #4420).
  // sendMessage is produced by useChat (declared below); the contact `add`
  // card action only needs it at click-time, so thread a stable ref-reader to
  // avoid the hook-ordering trap (same pattern as applyUiActionsRef/focusModuleRef).
  const sendMessageRef = useRef<(content: string, conversationId?: string) => Promise<void> | void>(() => {});
  const sendMessageStable = useCallback(
    (content: string, conversationId?: string) => sendMessageRef.current(content, conversationId),
    [],
  );
  const {
    applyUiActions,
    onCardAction,
    openWorkspaceModuleByLabel,
    getNextViewAckMessage,
    runLocalViewIntentAction,
    lastRouteModuleFocusKeyRef,
  } = useChatUiActions({
    activeConversation,
    availableModules,
    token,
    workspaceId,
    queryClient,
    navigate,
    selectObject,
    showToast,
    sendMessage: sendMessageStable,
    expandChatPane,
    deleteConversation,
    refreshWorkspaceModuleCatalog,
    ensureModulePanelInConversationOnce,
    refreshPanelBoundModules,
    invalidateConversationStructure,
    queuePendingModuleFocus,
    refreshSession,
    updateProfile,
    requestedRouteModuleId,
    routeConversationIdParam,
    setObjectCardPopover,
    setNoteModal,
    setDocModal,
    setIsLocalViewIntentThinking,
    workspaceModuleCatalogRef,
    moduleFocusSequenceRef,
    lastAppliedModuleFocusSequenceRef,
    activeConversationIdRef,
    focusModuleRef,
    viewIntentPhraseIndexRef,
    localViewIntentTimerRef,
  });

  // Stable ref to the two-way voice-mode controller. Declared here (before
  // useChat) so the extracted buildChatCallbacks can receive it eagerly; the
  // live value is assigned to `.current` after useVoiceMode runs below. The ref
  // identity is stable, and callbacks only read `.current` at invocation time,
  // so this preserves the original lazy-read semantics exactly.
  const voiceModeRef = useRef<any>(null);

  const {
    messages,
    thinking,
    streamActivity,
    sendMessage,
    retryLastMessage,
    addLocalMessages,
    addAssistantMessage,
    appendUserMessage,
    clearMessages,
    stopStreaming,
    isStreaming,
    isLoading,
    error,
    elapsedSeconds,
    pendingQuickActions,
    setPendingQuickActions,
    addQuickActions,
    retryBubble,
    dismissRetryBubble,
  } = useChat({
    sessionId,
    mode,
    model: specificModel,
    activeModuleId: effectiveActiveModuleId,
    activeModuleObjectType: effectiveActiveModuleObjectType,
    activeModuleLabel: effectiveActiveModuleLabel,
    activePageContext,
    activePageContextRef,
    activeDriveFolder,
    activeDriveFolderRef,
    activeObjectContext,
    // Event-callback block extracted verbatim to chatbox/chat-callbacks.ts.
    // Built fresh per render (same as the old inline object literal) so each
    // callback closes over the latest ChatBox state; refs are threaded through.
    ...buildChatCallbacks({
      activeConversation,
      workspaceId,
      queryClient,
      voiceModeRef,
      toolInvalidationTimerRef,
      postStreamEnrichmentTimersRef,
      invalidateConversationObjects,
      invalidateConversationStructure,
      scheduleAutoTitleFollowupRefresh,
      markCreated,
      applyUiActions,
    }),
  });

  // Keep the stable ref-reader (threaded into useChatUiActions above) pointed at
  // the live sendMessage now that useChat has produced it.
  sendMessageRef.current = sendMessage;

  // The legacy 5-state onboarding manager/driver/beat transcript was removed
  // (recipe-replacement, #4420): the recipe framework is now THE onboarding (D3).
  // Agent-emitted follow-up chips flow straight from useChat's pendingQuickActions
  // to MessageList. Stale onboarding.advance/complete directives are dropped in
  // apply-ui-actions (the 5-state route they drove is gone).

  // Cross-module follow-up chips (#2014) — bridge the Tasks-import window event → chat.
  useCrossModuleQuickActions(addQuickActions);

  // Demo-seed kill-switch (server-authoritative): when demo is unavailable
  // (config.demoSeedEnabled off → /demo-seed/status 404 → available:false), drop
  // the onboarding `demo.load` / `demo.switch-to-real` chips so the feature is
  // gone across the board, not just from the sidebar menu.
  const { available: demoAvailable } = useDemoMode();
  const visibleQuickActions = useMemo(
    () => filterDemoQuickActions(pendingQuickActions, demoAvailable),
    [demoAvailable, pendingQuickActions],
  );

  const upgradeErr = error as (Error & {
    upgradeRequired?: boolean;
    resetsAt?: string;
    errorCode?: string;
    retryAfterSec?: number;
    status?: number;
  }) | null;

  const [limitDismissed, setLimitDismissed] = useState(false);
  const [storageExceeded, setStorageExceeded] = useState(false);

  useEffect(() => {
    // Re-show modal on the next blocked send (per issue acceptance criterion).
    // Cleared whenever the error identity changes.
    setLimitDismissed(false);
  }, [upgradeErr]);

  const limitState = deriveLimitReachedState(upgradeErr, limitDismissed);

  useEffect(() => {
    if (limitState.open) {
      console.info('[usage-limit-modal] shown', {
        mode: limitState.mode,
        errorCode: limitState.errorCode,
      });
      window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
        detail: { action: 'shown', mode: limitState.mode, errorCode: limitState.errorCode },
      }));
    }
  }, [limitState.open, limitState.mode, limitState.errorCode]);

  // Two-way voice mode (must be after useChat so sendMessage is available).
  // `voiceModeRef` is declared above (before useChat) so the extracted
  // buildChatCallbacks can receive it eagerly without a temporal-dead-zone
  // reference; the live `voiceMode` value is still assigned to it here.
  const voiceMode = useVoiceMode({
    onSend: async (text: string) => {
      await sendMessage(text, activeConversation?.id, undefined, undefined, { voiceMode: true });
    },
    onTranscriptReady: (text: string) => {
      // Show the finalized transcript as concrete text in the input box
      setInputValue(text);
      setInterimTranscript('');
    },
    onTranscriptSent: () => {
      // Clear input after message is sent
      setInputValue('');
    },
    onAbortAgent: () => {
      // Barge-in: cancel the active SSE stream so stale responses stop arriving
      stopStreaming();
    },
    token,
    userName: profile?.name || '',
  });
  voiceModeRef.current = voiceMode;



  // Chat-window onboarding (replaces the card-on-wood recipe host). When a
  // brand-new user lands in the real chat with the sidebar hidden, the live
  // `onboardingActive` comes from OnboardingActiveContext (seeded by the App
  // gate). useChatOnboarding seeds the welcome message, then — after a short
  // beat, display-only and independent of data readiness (#5387) — the
  // look-around invite. It separately polls sync readiness and, on
  // ready (or the cap), reveals the sidebar (endOnboardingActive) + latches
  // completion. On the normal returning-user path no provider wraps ChatBox,
  // so the context default keeps this `false` — the downstream readers
  // (useChatScroll, ChatComposer) behave exactly as before.
  const { onboardingActive, endOnboardingActive } = useOnboardingActive();
  const { reviseActive, activeApprovalId: activeReviseApprovalId, setReviseConversationId } = useReviseSession();
  // Idempotency-against-the-thread predicate for onboarding seeding. The hook
  // repoints its bindings every render, so this fresh closure always reads the
  // CURRENT `messages` (the loaded thread). Returns true when an
  // onboarding-routed assistant message with exactly `content` is already
  // present — so a re-seed after an HMR reset / hard reload no-ops instead of
  // duplicating the welcome/look-around lines.
  const hasOnboardingMessage = useCallback(
    (content: string) =>
      messages.some(
        (m) =>
          m.role === 'assistant' &&
          m.routing === 'onboarding' &&
          m.content === content,
      ),
    [messages],
  );
  // Ref-backed "which thread is on screen" accessor for the post-reveal letter
  // seed (#5226) — safe to read from polls scheduled on an earlier mount.
  const getActiveConversationId = useCallback(
    () => activeConversationIdRef.current,
    [],
  );
  // Context-selected onboarding recipe (welcome + invite copy). Priority: explicit `?ob`
  // URL param → signed-in email domain → default. Resolved once and memoized so
  // it's a stable reference across renders/remounts (the hook stores it in its
  // module-level session bindings). The `default` recipe = identical to prior copy.
  //
  // The `?ob` param is read via readOnboardingObParam — which prefers the value
  // captured at app module-eval (App.tsx) and persisted in sessionStorage — NOT a
  // live read of window.location.search. On the merged redirect flow ChatBox can
  // mount BEFORE `?ob` is in the URL, so a live read would memoize null → default;
  // the captured value makes resolution immune to that mount-timing race.
  const onboardingRecipe = useMemo(() => {
    return resolveOnboardingRecipe({
      obParam: readOnboardingObParam(),
      email: profile?.email ?? null,
    });
  }, [profile?.email]);
  useChatOnboarding({
    onboardingActive,
    conversationId: activeConversation?.id,
    // Gate seeding on the thread's initial load completing so a hard reload
    // mid-onboarding can't re-seed the welcome before the persisted copy lands.
    messagesReady: !isLoading,
    token,
    workspaceId,
    recipe: onboardingRecipe,
    addAssistantMessage,
    hasOnboardingMessage,
    endOnboardingActive,
    refreshSession,
    getActiveConversationId,
  });

  // Returning-user "New Chat" greeting. Onboarding conversations are seeded by
  // useChatOnboarding above (gated on onboardingActive); this restores the
  // regular-chat welcome that #4598 dropped, so an existing user never lands in a
  // blank chat. Gated on a real (non-temp) id, an empty thread, and a settled
  // load/stream — see the hook for the #1583 race protections.
  useNewChatGreeting({
    conversationId: activeConversation?.id,
    messagesLength: messages.length,
    isLoading,
    isStreaming,
    hasProfile: Boolean(profile),
    onboardingActive,
    addAssistantMessage,
    greetedConversationIds,
  });

  // Onboarding is just a welcome + invite message — there are NO suggestion
  // pills anymore, so there's nothing to derive/re-apply. Clear the persisted
  // `?ob` recipe param once onboarding ends (onboardingActive SET→false), so the
  // sessionStorage key doesn't linger and steer a later onboarding's welcome
  // wording. (The recipe still selects the welcome copy while onboarding is
  // active, so we clear only on the true end, not mid-flow.)
  const wasOnboardingActiveRef = useRef(false);
  useEffect(() => {
    if (wasOnboardingActiveRef.current && !onboardingActive) {
      clearOnboardingObParam();
    }
    wasOnboardingActiveRef.current = onboardingActive;
  }, [onboardingActive]);

  // Run a slash command programmatically (command-launcher card rows). Routes
  // through the composer form so parseInput handles it EXACTLY like a typed
  // command — same mechanism as the autocomplete dropdown's click handler
  // (set the input, then dispatch submit once state has flushed). Calling the
  // raw sendMessage would post the literal "/today" to the agent instead.
  const runSlashCommand = useCallback((cmd: string) => {
    setInputValue(cmd);
    setTimeout(() => {
      const form = chatInputRef.current?.closest('form');
      form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, 0);
  }, []);

  // Mobile chrome side-effects: composer auto-focus, --mobile-bottom-bar-height
  // tracking, fullscreen-module body class (see hooks/useMobileChatChrome).
  useMobileChatChrome({
    isMobile,
    activeConversationId: activeConversation?.id,
    isStreaming,
    isLoading,
    isMobileModuleView,
    isModulePaneVisible,
    chatInputRef,
    bottomStackRef,
  });

  useEffect(() => {
    latestMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
  }, [messages]);

  // Archive inline modules (from sidebar clicks) that differ from the currently focused
  // pane module, so only one module shows at a time.
  useEffect(() => {
    if (!chatPaneModuleId || !isModulePaneVisible) return;
    setArchivedInlineModuleIds((prev) => {
      const toArchive = messages
        .map((m) => m.inlineModuleId)
        .filter((id): id is string => Boolean(id) && id !== chatPaneModuleId);
      if (toArchive.length === 0) return prev;
      const next = new Set(prev);
      toArchive.forEach((id) => next.add(id));
      if (next.size === prev.size) return prev; // No new IDs — keep same reference
      return next;
    });
  }, [chatPaneModuleId, isModulePaneVisible, messages]);

  useEffect(() => {
    if (!activeConversation?.id || !moduleEmbedAnchorMessageId) return;
    moduleEmbedAnchorByConversationRef.current[activeConversation.id] = moduleEmbedAnchorMessageId;
  }, [activeConversation?.id, moduleEmbedAnchorMessageId]);

  useEffect(() => {
    if (isMobile) return; // Skip on mobile — module pane persistence causes it to pop over chat
    if (!isEmbeddedModulePaneEnabled || !shouldShowModulePane) return;
    if (moduleEmbedAnchorMessageId) return;
    if (!latestMessageIdRef.current) return;
    const fallbackAnchorMessageId = latestMessageIdRef.current;
    setModuleEmbedAnchorMessageId(fallbackAnchorMessageId);
    if (activeConversation?.id) {
      moduleEmbedAnchorByConversationRef.current[activeConversation.id] = fallbackAnchorMessageId;
    }
    if (activeConversation?.id && chatPaneModuleId) {
      void persistModulePaneState(activeConversation.id, chatPaneModuleId, {
        isVisible: true,
        isFullscreen: true,
        anchorMessageId: fallbackAnchorMessageId,
      });
    }
  }, [
    activeConversation?.id,
    chatPaneModuleId,
    isMobile,
    isEmbeddedModulePaneEnabled,
    moduleEmbedAnchorMessageId,
    messages.length,
    persistModulePaneState,
    shouldShowModulePane,
  ]);

  useEffect(() => {
    if (isMobile) return; // Skip on mobile — module pane persistence causes it to pop over chat
    if (!isEmbeddedModulePaneEnabled || !shouldShowModulePane) return;
    if (!moduleEmbedAnchorMessageId) return;
    if (messages.some((message) => message.id === moduleEmbedAnchorMessageId)) return;

    // Heal stale anchors (e.g., old local temp ids) by snapping to latest assistant
    // so the module appears after the latest assistant acknowledgement.
    const latestAssistantMessageId = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant')?.id
      ?? latestMessageIdRef.current;
    if (!latestAssistantMessageId) return;

    setModuleEmbedAnchorMessageId(latestAssistantMessageId);
    if (activeConversation?.id) {
      moduleEmbedAnchorByConversationRef.current[activeConversation.id] = latestAssistantMessageId;
    }
    if (activeConversation?.id && chatPaneModuleId) {
      void persistModulePaneState(activeConversation.id, chatPaneModuleId, {
        isVisible: true,
        isFullscreen: true,
        anchorMessageId: latestAssistantMessageId,
      });
    }
  }, [
    activeConversation?.id,
    chatPaneModuleId,
    isMobile,
    isEmbeddedModulePaneEnabled,
    messages,
    moduleEmbedAnchorMessageId,
    persistModulePaneState,
    shouldShowModulePane,
  ]);

  // Periodic data refresh while agent is streaming (covers internal tool loops)
  // x-agent handles tools server-side, so the adapter doesn't emit tool_start events.
  // Poll every 5 seconds to pick up data changes the agent makes during long operations.
  useEffect(() => {
    if (!isStreaming || !activeConversation?.id) return;

    // First refresh after 3 seconds (quick feedback for simple tool calls)
    const initialTimer = setTimeout(() => {
      invalidateConversationObjects(activeConversation.id);
    }, 3000);

    // Then every 5 seconds for longer operations
    const interval = setInterval(() => {
      invalidateConversationObjects(activeConversation.id);
    }, 5000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isStreaming, activeConversation?.id, invalidateConversationObjects]);

  useFinalCacheInvalidationOnStreamEnd(isStreaming, activeConversation?.id, invalidateConversationObjects);

  // Modules open only on an explicit agent module.focus action — never auto-surfaced
  // after a reply (that covered the chat response on mobile).

  // @-mention autocomplete (issue #1457): owns mention state, debounced
  // hybridSearch, detection, keyboard-select, and drag-drop apply. ChatBox just
  // threads its outputs into the composer + drop target.
  const mention = useMentionAutocomplete({
    inputValue,
    setInputValue,
    chatInputRef,
    activeConversationId: activeConversation?.id,
  });

  // File-attachment pipeline (useImport + handleFileSelect/handlePaste/
  // handleChatDragOver/handleChatDrop) extracted verbatim to
  // hooks/useChatAttachments.ts — same bodies, same byte-identical dep arrays.
  const {
    importPhase,
    importingFiles,
    importProgress,
    isImporting,
    handleFileSelect,
    handlePaste,
    handleChatDragOver,
    handleChatDrop,
  } = useChatAttachments({
    activeConversation,
    token,
    workspaceId,
    queryClient,
    addAssistantMessage,
    setPendingAttachments,
    setPastedAttachments,
    setStorageExceeded,
    applyDroppedReference: mention.applyDroppedReference,
  });

  // Window-event listener cluster (5 useEffects) extracted verbatim to
  // hooks/useChatWindowEvents.ts — same bodies, same byte-identical dep arrays,
  // same relative hook order. sendMessage / applyUiActions / appendUserMessage /
  // refreshSession / onboarding are threaded in; refs + setter by reference.
  useChatWindowEvents({
    activeConversation,
    lastCreatedConversationId,
    lastCreatedConversationOriginId,
    sendMessage,
    appendUserMessage,
    messages,
    applyUiActions,
    userHasScrolledRef,
    lastRouteModuleFocusKeyRef,
    setArchivedInlineModuleIds,
    addAssistantMessage,
    markConversationGreeted: (id: string) => { greetedConversationIds.add(id); },
    activeReviseApprovalId,
    setReviseConversationId,
  });

  // Speech dictation cluster (ref-sync + recognition init + onresult append
  // semantics) now lives in useSpeechRecognition; `appendDictation` is imported
  // from chatbox/speech-dictation for the composer prop below.

  const micGesture = useGesture({
    onSingleTap: () => {
      if (!voiceMode.isActive) {
        toggleVoiceInput();
      }
    },
    onDoubleTap: () => {
      if (!voiceMode.isActive) {
        if (isListening) {
          stopDictation();
        }
        voiceMode.enter();
      }
    },
    onLongPress: () => {
      if (!voiceMode.isActive) {
        if (isListening) {
          stopDictation();
        }
        voiceMode.enter();
      }
    },
  });

  // Sync voice mode transcripts into the existing dictation state
  // so the exact same ghost text rendering works for both modes
  useEffect(() => {
    if (!voiceMode.isActive) return;
    if (voiceMode.phase === 'listening') {
      // Finalized words go into inputValue (solidified text)
      if (voiceMode.userTranscript) {
        setInputValue(voiceMode.userTranscript);
        // Move textarea caret to end so the visible cursor follows the
        // dictated text instead of staying at position 0.
        requestAnimationFrame(() => {
          const ta = chatInputRef.current;
          if (ta) {
            const end = ta.value.length;
            ta.selectionStart = end;
            ta.selectionEnd = end;
          }
        });
      }
      // Interim words shown as ghost
      setInterimTranscript(voiceMode.interimTranscript);
    }
  }, [voiceMode.isActive, voiceMode.phase, voiceMode.userTranscript, voiceMode.interimTranscript]);

  // Show dictation UI (waveform, cancel button) during entire voice session, not just listening phase
  const isAnyListening = isListening || voiceMode.isActive;
  const activeInterimTranscript = interimTranscript;

  const cancelVoiceInput = () => {
    // Also exit voice mode if active
    if (voiceMode.isActive) {
      voiceMode.exit();
    }
    stopDictation();
  };

  const handleViewIntent = useCallback((content: string): boolean => {
    return runHandleViewIntent(content, {
      activeConversation,
      addLocalMessages,
      dispatchModuleCommand,
      getNextViewAckMessage,
      runLocalViewIntentAction,
      focusModuleRef,
    });
  }, [
    activeConversation,
    addLocalMessages,
    dispatchModuleCommand,
    getNextViewAckMessage,
    runLocalViewIntentAction,
  ]);

  // Fast-path: "open [name]" asks the active module to find and open an object by name.
  // Uses a synchronous window event so the module (which owns the data) resolves the match.
  const handleOpenObjectIntent = useCallback((content: string): boolean => {
    return runHandleOpenObjectIntent(content, {
      activeConversation,
      addLocalMessages,
      runLocalViewIntentAction,
    });
  }, [activeConversation, addLocalMessages, runLocalViewIntentAction]);

  const handleFilterIntent = useCallback((content: string): boolean => {
    return runHandleFilterIntent(content, {
      activeConversation,
      addLocalMessages,
      dispatchModuleCommand,
      token,
    });
  }, [activeConversation, addLocalMessages, dispatchModuleCommand, token]);

  const clearChatAndCloseModulePane = useCallback(() => {
    clearMessages();
    hideModulePane();
    setChatPaneModuleId(null);
    pendingAttachedModuleFocusQueueRef.current = [];
    conversationContextSentRef.current = null;
    if (activeConversation?.id) {
      greetedConversationIds.delete(activeConversation.id);
    }
  }, [clearMessages, hideModulePane, activeConversation?.id]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    clearChat: () => {
      clearChatAndCloseModulePane();
    },
    syncMode: () => {
      // No-op: mode is hardcoded to API
    },
    addAssistantMessage: (content: string, conversationId?: string) => {
      addAssistantMessage(content, conversationId);
    },
    sendMessage: async (content: string, conversationId?: string) => {
      // Send message through the chat system
      const targetConversationId = conversationId || activeConversation?.id;
      await sendMessage(content, targetConversationId);
    },
  }), [activeConversation, addAssistantMessage, clearChatAndCloseModulePane, sendMessage]);

  // (The old per-conversation "AI Briefing" generator was removed — schedule
  // asks now render the Today card via the agent's today_card tool.)

  // Scroll cluster extracted as ONE unit (Stage 3). Refs stay created in the
  // orchestrator (the conversation-change reset effect + send path read them),
  // so they are passed in and never recreated. Dep arrays are byte-identical.
  useChatScroll({
    messagesContainerRef,
    userHasScrolledRef,
    isInitialLoadRef,
    forceBottomRef,
    activeConversation,
    isStreaming,
    isLoading,
    messages,
    setIsScrollSwitchSettling,
    onboardingActive,
  });

  // Process queued messages when streaming ends
  useEffect(() => {
    if (!isStreaming && messageQueueRef.current.length > 0) {
      const nextMessage = messageQueueRef.current.shift();
      if (nextMessage) {
        // Small delay to let UI settle
        setTimeout(() => {
          const queuedOptions = {
            hideUserMessage: true, // Already shown via addLocalMessages when queued
            ...(nextMessage.images?.length ? { images: nextMessage.images } : {}),
            ...(nextMessage.documents?.length ? { documents: nextMessage.documents } : {}),
            ...(nextMessage.attachments?.length ? { attachments: nextMessage.attachments } : {}),
            ...(nextMessage.pastedAttachments?.length ? { pastedAttachments: nextMessage.pastedAttachments } : {}),
          };
          sendMessage(nextMessage.content, nextMessage.conversationId, undefined, nextMessage.context, queuedOptions);
        }, 100);
      }
    }
  }, [isStreaming, sendMessage]);

// AUTO-SPEAK DISABLED: Greetings removed entirely per design rules
  // XAVIOR should be silent by default. The input box communicates "I'm here when you need me."
  // Auto-speak should only fire on real state deltas (tasks due, conflicts, changes)
  // NOT on: new chat, conversation switch, navigation, or any passive UI action
  //
  // If we want auto-speak in the future, it must:
  // 1. Reference actual conversation state (e.g., "3 tasks due today")
  // 2. Never ask questions or greet
  // 3. Be a single factual sentence
  // 4. Only trigger on first app load of the day with real data to share


  // Composer input plumbing (3 useEffects: name-focus, refocus-on-stream-end,
  // resize-on-input + resizeTextarea / name handlers / handleKeyDown) extracted
  // verbatim to hooks/useChatComposerInput.ts. handleKeyDown also drives the
  // @-mention dropdown keyboard nav (issue #1457), ahead of the command nav.
  const {
    resizeTextarea,
    handleKeyDown,
  } = useChatComposerInput({
    isEditingName,
    setIsEditingName,
    setUserName,
    isMobile,
    isStreaming,
    isLoading,
    activeConversation,
    inputValue,
    setInputValue,
    nameInputRef,
    chatInputRef,
    showCommandDropdown,
    commandMatches,
    selectedCommandIndex,
    setShowCommandDropdown,
    setCommandMatches,
    setSelectedCommandIndex,
    showMentionDropdown: mention.showMentionDropdown,
    mentionMatchCount: mention.mentionMatches.length,
    setSelectedMentionIndex: mention.setSelectedMentionIndex,
    selectMention: mention.selectMention,
    closeMentionDropdown: mention.closeMentionDropdown,
  });

  // Slash-command + quick-create wrapper callbacks (delegation pattern).
  // Extracted into useChatCommands — the bodies + dependency arrays are
  // preserved verbatim there; this single hook call sits at the same stable
  // position the inline useCallbacks did.
  const {
    handleModuleSelection,
    handleConversationCommand,
    handleConversationsCommand,
    handleSearchCommand,
    handleTaskCommand,
    handleNoteCommand,
    handleContactCommand,
    handleEventCommand,
  } = useChatCommands({
    token,
    activeConversation,
    availableModules,
    conversationJson,
    allConversations,
    addLocalMessages,
    queryClient,
    pendingModuleSelection,
    setPendingModuleSelection,
  });

  // Stable-identity latches for the event-time-only handlers MessageList
  // receives. sendMessage (and everything downstream of it) is rebuilt every
  // render by design (buildChatCallbacks closes over fresh state), which would
  // defeat MessageList's React.memo — these keep the prop identity stable
  // while always invoking the latest closure (#5255, F-performance-09).
  const sendMessageForList = useLatestCallback(sendMessage);
  const retryLastMessageForList = useLatestCallback(retryLastMessage);
  const applyUiActionsForList = useLatestCallback(applyUiActions);
  const onCardActionForList = useLatestCallback(onCardAction);
  const openWorkspaceModuleByLabelForList = useLatestCallback(openWorkspaceModuleByLabel);
  const handleModuleSelectionForList = useLatestCallback(handleModuleSelection);

  // Composer submit handler. Body extracted verbatim to useChatSubmit; this
  // hook is called once per render with fresh deps and returns a fresh handler,
  // preserving the original plain-function-per-render closure semantics.
  const handleSubmit = useChatSubmit({
    isListening,
    stopDictation,
    pendingAttachments,
    pastedAttachments,
    addLocalMessages,
    clearChatAndCloseModulePane,
    openModal,
    handleConversationCommand,
    handleConversationsCommand,
    handleSearchCommand,
    handleTaskCommand,
    handleNoteCommand,
    handleContactCommand,
    handleEventCommand,
    handleFilterIntent,
    handleOpenObjectIntent,
    handleViewIntent,
    activeConversation,
    conversationContextSentRef,
    conversationJson,
    messages,
    token,
    workspaceId,
    queryClient,
    selectedEditSections,
    isStreaming,
    messageQueueRef,
    sendMessage,
    lastSentRef,
    recentlySentFilesRef,
    setPendingAttachments,
    setPastedAttachments,
    setExpandedPastedAttachmentIds,
    setInputValue,
    setSelectedEditSections,
    chatInputRef,
    closeMentionDropdown: mention.closeMentionDropdown,
  });

  const placeholder = isReadOnly
    ? 'View only - chat disabled'
    : activeConversation
      ? 'Ask me to do anything'
      : 'Select a conversation to start chatting...';
  const isModulePaneFullscreenActive =
    !isMobile && isModulePaneVisible && Boolean(selectedChatPaneModule);

  // Chat-dock wiring (handleDockChat + isChatDockSplitActive + dock layout-effect)
  // extracted verbatim to hooks/useChatDockWiring.ts — same view-transition/FLIP
  // semantics. isModulePaneFullscreenActive stays derived here and threads in.
  const {
    handleDockChat,
    isChatDockSplitActive,
  } = useChatDockWiring({
    isModulePaneFullscreenActive,
    isDesktopSplitViewport,
    chatDockState,
    dockChat,
    expandChatPane,
    setChatDockSplitViewActive,
    pendingOpenFocus,
    ackOpenFocus,
    pendingLandingReveal,
    ackLandingReveal,
    registerUndockRectProvider,
    chatBoxRootRef,
    chatInputRef,
  });
  // Esc closes the open module pane + refocuses chat (issue #3938, modal/editor-safe).
  useModulePaneEscapeClose({ isModulePaneVisible, chatInputRef });
  // Publish the raw module-pane-open flag (independent of the ≥768 desktop
  // split breakpoint, unlike isChatDockSplitActive) so MainLayout's header
  // "open chat" button can key off it. Reset on unmount.
  useEffect(() => {
    setModulePaneOpen(isModulePaneFullscreenActive);
    return () => setModulePaneOpen(false);
  }, [isModulePaneFullscreenActive, setModulePaneOpen]);
  // Pre-return derived-render block (isEmptyChat, thinking timeline,
  // selectedChatPaneModuleTitle, modulePaneElement, modulePanePortalTarget)
  // extracted verbatim to hooks/useChatRenderState.tsx (a .tsx because
  // modulePaneElement renders ChatModulePane) — same derivations + dep arrays.
  const {
    isEmptyChat,
    isPreEngagementChat,
    shouldShowThinkingTimeline,
    thinkingTimelineSteps,
    selectedChatPaneModuleTitle,
    modulePaneElement,
    modulePanePortalTarget,
  } = useChatRenderState({
    shouldShowAnyPane,
    isStreaming,
    isLocalViewIntentThinking,
    isLoading,
    messages,
    streamActivity,
    thinking,
    elapsedSeconds,
    selectedChatPaneModule,
    shouldShowModulePane,
    activeConversation,
    isMobile,
    isModulePaneFullscreenActive,
    hideModulePane,
    moduleMenuCloseSignal,
    showIntegrationAccountsModal,
    showManageAccountsModal,
    setShowIntegrationAccountsModal,
    setShowManageAccountsModal,
    modulePaneCardRef,
    modulePaneTitleRef,
    chatBoxRootRef,
  });

  useEffect(() => {
    if (wasModulePaneSplitRef.current && !isModulePaneFullscreenActive) {
      dispatchWindowEvent(conversationEvents.setSidebarCollapsed, { collapsed: false });
    }
    wasModulePaneSplitRef.current = isModulePaneFullscreenActive;
  }, [isModulePaneFullscreenActive]);

  useEffect(() => {
    return () => {
      if (wasModulePaneSplitRef.current) {
        dispatchWindowEvent(conversationEvents.setSidebarCollapsed, { collapsed: false });
      }
    };
  }, []);

  return (
    <div
      id="chat-box-root"
      ref={chatBoxRootRef}
      className={`chat-box ${isMobile ? 'chat-box-mobile' : ''} ${isEmptyChat ? 'chat-box-empty' : ''} ${isModulePaneFullscreenActive ? 'chat-box-module-fullscreen' : ''} ${isModulePaneFullscreenActive && isChatPaneCollapsed ? 'chat-pane-collapsed' : ''} ${isModulePaneFullscreenActive && chatDockState === 'docking' ? 'chat-dock-docking' : ''} ${isModulePaneFullscreenActive && chatDockState === 'undocking' ? 'chat-dock-undocking' : ''} ${isModulePaneFullscreenActive && chatDockState === 'open' && pendingLandingReveal ? 'chat-dock-landing' : ''} ${isMobileModuleView ? 'chat-mobile-module-view' : ''} ${isMobileChatWithModule ? 'chat-mobile-chat-with-module' : ''} ${reviseActive ? 'is-revise-split' : ''}`}
      onDragOver={handleChatDragOver}
      onDrop={handleChatDrop}
      onKeyDown={(e) => {
        // Escape inside the chat panel docks it into the header (split view
        // only). Bound at the root so it covers the composer too (the
        // composer is a sibling of .chat-content-shell). Inner Escape
        // consumers (command dropdown) preventDefault so they win over the
        // dock; the module pane is portaled into this root, so Escape there
        // is explicitly excluded — closing the chat from inside the module
        // would be a surprise.
        if (e.key !== 'Escape' || e.defaultPrevented) return;
        if (!isChatDockSplitActive || chatDockState !== 'open') return;
        if ((e.target as HTMLElement | null)?.closest?.('.chat-module-pane-card')) return;
        e.preventDefault();
        handleDockChat();
      }}
    >
      {/* The Nordic "desk" wood now lives at the MainLayout shell level (behind
          the whole app), so the chat pane just stays transparent over it. */}

      {/* Desktop header (logo + XAVIOR 2.1 + bell) now lives in MainLayout's
          full-width .app-header above the rail + chat. No per-pane header here. */}

      {/* ONE pane card: the chat side (messages shell + import indicator +
          composer) as a single real box in the split grid (display:contents
          everywhere else), so the view transition captures the whole panel
          as one snapshot and it moves as one rigid container.
          Chrome extracted to chatbox/ChatPaneCard (slot-shell): the
          <MessageList/> + <ChatComposer/> elements stay BUILT here so their
          ~100 bindings never become props. */}
      <ChatPaneCard
        isChatDockSplitActive={isChatDockSplitActive}
        chatDockState={chatDockState}
        handleDockChat={handleDockChat}
        showQuotaWarnBanner={showQuotaWarnBanner}
        quotaWarnPct={quotaWarnPct}
        quotaWarnWindow={quotaWarnWindow}
        setQuotaWarnDismissed={setQuotaWarnDismissed}
        messagesContainerRef={messagesContainerRef}
        compactMode={compactMode}
        isScrollSwitchSettling={isScrollSwitchSettling}
        isEmptyChat={isEmptyChat}
        isImporting={isImporting}
        importPhase={importPhase}
        importProgress={importProgress}
        importingFiles={importingFiles}
        bottomStackRef={bottomStackRef}
        isMobileChatWithModule={isMobileChatWithModule}
        selectedChatPaneModule={selectedChatPaneModule}
        selectedChatPaneModuleTitle={selectedChatPaneModuleTitle}
        onOpenMobileModule={() => setMobileView('module')}
        onCloseModule={hideModulePane}
        composerVisible={!isMobileModuleView && !(isMobile && isModulePaneVisible && selectedChatPaneModule?.type === 'channels')}
        messageList={
        <>
        {/* New-chat connect affordance: shown only in the empty/new-chat view, for
            workspaces with no email/calendar provider connected. Renders null
            otherwise (loading, or already connected). */}
        {/* Onboarding owns its own connect affordance (the recipe flow);
            never flash the empty-chat "Connect your email" CTA over the seeding
            welcome/look-around lines while onboardingActive (#4598 follow-up). */}
        {/* Use isPreEngagementChat (not isEmptyChat) so the returning-user welcome
            greeting doesn't suppress this CTA for an unconnected user — the greeting
            and the connect nudge coexist until the user sends their first message. */}
        {!onboardingActive && isPreEngagementChat && (
          <ChatConnectPrompt conversationId={activeConversation?.id} />
        )}
        <MessageList
          voiceMode={voiceMode}
          messages={messages}
          retryLastMessage={retryLastMessageForList}
          dismissRetryBubble={dismissRetryBubble}
          activeConversation={activeConversation}
          sendMessage={sendMessageForList}
          copiedMessageId={copiedMessageId}
          setCopiedMessageId={setCopiedMessageId}
          clickableModuleLabels={clickableModuleLabels}
          openWorkspaceModuleByLabel={openWorkspaceModuleByLabelForList}
          setObjectCardPopover={setObjectCardPopover}
          availableModulesRef={availableModulesRef}
          workspaceModuleCatalogRef={workspaceModuleCatalogRef}
          applyUiActions={applyUiActionsForList}
          selectObject={selectObject}
          onCardAction={onCardActionForList}
          openModal={openModal}
          archivedInlineModuleIds={archivedInlineModuleIds}
          setArchivedInlineModuleIds={setArchivedInlineModuleIds}
          isModulePaneVisible={isModulePaneVisible}
          chatPaneModuleId={chatPaneModuleId}
          availableModules={availableModules}
          setChatPaneModuleId={setChatPaneModuleId}
          setIsModulePaneVisible={setIsModulePaneVisible}
          setModuleEmbedAnchorMessageId={setModuleEmbedAnchorMessageId}
          token={token}
          workspaceId={workspaceId}
          queryClient={queryClient}
          quickActions={visibleQuickActions}
          isStreaming={isStreaming}
          recentlySentFilesRef={recentlySentFilesRef}
          showToast={showToast}
          setChatImportCSVFile={setChatImportCSVFile}
          confirmOAuthConsent={confirmOAuthConsent}
          shouldShowThinkingTimeline={shouldShowThinkingTimeline}
          thinkingTimelineSteps={thinkingTimelineSteps}
          pendingModuleSelection={pendingModuleSelection}
          handleModuleSelection={handleModuleSelectionForList}
          setPendingModuleSelection={setPendingModuleSelection}
          error={error}
          messagesEndRef={messagesEndRef}
        />
        </>
        }
        composer={
        <ChatComposer
          handleSubmit={handleSubmit}
          showCommandDropdown={showCommandDropdown}
          commandMatches={commandMatches}
          selectedCommandIndex={selectedCommandIndex}
          setShowCommandDropdown={setShowCommandDropdown}
          setCommandMatches={setCommandMatches}
          setSelectedCommandIndex={setSelectedCommandIndex}
          setInputValue={setInputValue}
          chatInputRef={chatInputRef}
          inputValue={inputValue}
          resizeTextarea={resizeTextarea}
          isAnyListening={isAnyListening}
          activeInterimTranscript={activeInterimTranscript}
          isListening={isListening}
          interimTranscript={interimTranscript}
          appendDictation={appendDictation}
          voiceMode={voiceMode}
          placeholder={placeholder}
          activeConversation={activeConversation}
          hasNoConversations={hasNoConversations}
          isLoading={isLoading}
          isStreaming={isStreaming}
          isReadOnly={isReadOnly}
          isImporting={isImporting}
          handleKeyDown={handleKeyDown}
          handlePaste={handlePaste}
          isMobile={isMobile}
          shouldShowAnyPane={shouldShowAnyPane}
          setMobileView={setMobileView}
          sessionId={sessionId}
          fileInputRef={fileInputRef}
          handleFileSelect={handleFileSelect}
          analyser={analyser}
          cancelVoiceInput={cancelVoiceInput}
          micGesture={micGesture}
          stopStreaming={stopStreaming}
          lastSentRef={lastSentRef}
          pendingAttachments={pendingAttachments}
          selectedEditSections={selectedEditSections}
          setSelectedEditSections={setSelectedEditSections}
          selectedChatPaneModuleTitle={selectedChatPaneModuleTitle}
          pastedAttachments={pastedAttachments}
          expandedPastedAttachmentIds={expandedPastedAttachmentIds}
          setExpandedPastedAttachmentIds={setExpandedPastedAttachmentIds}
          setPastedAttachments={setPastedAttachments}
          setPendingAttachments={setPendingAttachments}
          onboardingActive={onboardingActive}
          showMentionDropdown={mention.showMentionDropdown}
          mentionMatches={mention.mentionMatches}
          selectedMentionIndex={mention.selectedMentionIndex}
          isMentionLoading={mention.isMentionLoading}
          handleMentionInput={mention.handleMentionInput}
          selectMention={mention.selectMention}
          setSelectedMentionIndex={mention.setSelectedMentionIndex}
        />
        }
      />

      {/* Portal: renders module into embed slot (inline anchor) or chatbox root (split grid) */}
      {modulePaneElement && modulePanePortalTarget
        ? createPortal(modulePaneElement, modulePanePortalTarget)
        : null}

      {/* Traveling surface of the dock/undock shared-element morph (portaled
          to <body> so it flies above the fixed header, unclipped). */}
      {isModulePaneFullscreenActive
        && (chatDockState === 'docking' || chatDockState === 'undocking')
        && chatDockFlightRect && (
        <ChatDockFlight
          rect={chatDockFlightRect}
          mode={chatDockState}
          onArrived={finishUndock}
        />
      )}

      {/* Chat-driven CSV import — opened by the contacts.import-csv quick
          action chip the agent emits when the user uploads a CSV/VCF. Calls
          /bulk/objects directly so it works without any Contacts module
          attached to the conversation (modules are workspace-level). */}
      {chatImportCSVFile && (
        <ChatCsvImportModal
          file={chatImportCSVFile}
          token={token}
          workspaceId={workspaceId}
          showToast={showToast}
          queryClient={queryClient}
          onImported={({ imported, failed, followUps, contacts }) => {
            // Close the loop the chip opened: post a confirmation so the chat
            // shows a done state instead of holding the agent's "want me to
            // import it?" question, and swap the answered prompt chips for the
            // result-aware follow-ups.
            if (imported > 0 || failed > 0) {
              const summary =
                failed === 0
                  ? `Imported ${imported} contact${imported === 1 ? '' : 's'} into Contacts. ✓`
                  : `Imported ${imported} contact${imported === 1 ? '' : 's'} into Contacts${failed ? ` — ${failed} row${failed === 1 ? '' : 's'} couldn't be added` : ''}.`;
              // Show the created contacts as cards in the chat (parity with the
              // agent-driven PDF/bulk import). The RESULT_CARDS_MARKER block is
              // split + rendered by MessageList at render time.
              const cardBlock =
                contacts && contacts.length > 0
                  ? `\n${RESULT_CARDS_MARKER}${JSON.stringify([{ kind: 'contact', data: { contacts } }])}`
                  : '';
              addAssistantMessage(summary + cardBlock, activeConversation?.id);
            }
            setPendingQuickActions(followUps);
          }}
          onClose={() => setChatImportCSVFile(null)}
        />
      )}

      <ChatModalsLayer
        pendingOAuthConsent={pendingOAuthConsent}
        setPendingOAuthConsent={setPendingOAuthConsent}
        storageExceeded={storageExceeded}
        setStorageExceeded={setStorageExceeded}
        limitState={limitState}
        setLimitDismissed={setLimitDismissed}
        objectCardPopover={objectCardPopover}
        setObjectCardPopover={setObjectCardPopover}
        noteModal={noteModal}
        setNoteModal={setNoteModal}
        docModal={docModal}
        setDocModal={setDocModal}
        applyUiActions={applyUiActions}
        activeConversationId={activeConversation?.id}
      />
    </div>
  );
});

export default ChatBox;
